import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EVENT_STATUS_PILL, TagPill, PillButton, MUTED, RED } from './clubUi';
import { whenLabel, money } from '../../utils/surfaceFormat';
import { posterPlaceholder } from '../../utils/posterPlaceholder';

/**
 * One event row (README §1 NEXT UP / EVENTS): a grey radius-22 band with the 96px radius-16
 * poster, `SAT OCT 10 · 7:00 PM` + the status pill (+ a MEMBERS ONLY tag), the title 15/700,
 * the meta line, then GET TICKETS / CHIP IN and, for admins, HIDE · UNLINK.
 */
export function eventStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'canceled' || s === 'cancelled') return 'CANCELLED';
  if (s === 'processing_greenlight') return 'GREENLIT';
  if (s === 'completed') return 'ENDED';
  return (s || 'draft').replace(/_/g, ' ').toUpperCase();
}

export function eventMeta(e) {
  const parts = [];
  if (e.ticketPriceCents != null) parts.push(e.ticketPriceCents === 0 ? 'FREE' : money(e.ticketPriceCents));
  if (e.acceptedCount != null && e.maxAttendees) parts.push(`${e.acceptedCount}/${e.maxAttendees} GUESTS`);
  else if (e.fundingGoalCents > 0) parts.push(`${money(e.totalPledgedCents || 0)} OF ${money(e.fundingGoalCents)} FUNDED`);
  if (e.hostDisplayName) parts.push(`HOSTED BY ${e.hostDisplayName}`);
  if (e.locationCity) parts.push(e.locationCity);
  return parts.join(' · ').toUpperCase();
}

export default function ClubEventRow({ event, cta = true, ctaFill = true, admin = false, armedUnlink = false, onHide, onUnlink, phone = false, testId }) {
  const navigate = useNavigate();
  const to = `/events/${event.inviteCode || event.id}`;
  const [sBg, sFg] = EVENT_STATUS_PILL[String(event.status || 'draft').toLowerCase()] || EVENT_STATUS_PILL.draft;
  const hidden = !!event.isHiddenInClub;
  const upcoming = !['ended', 'completed', 'canceled', 'cancelled', 'dissolved'].includes(String(event.status || '').toLowerCase());
  const meta = [eventMeta(event), hidden ? 'HIDDEN FROM MEMBERS' : null].filter(Boolean).join(' · ');
  return (
    <div className="flex items-center font-mono" style={{ flexWrap: phone ? 'wrap' : 'nowrap', gap: 14, padding: '8px 14px 8px 8px', borderRadius: 22, background: '#F5F5F5', opacity: hidden ? 0.55 : 1 }} data-testid={testId || `event-row-${event.id}`}>
      <button type="button" onClick={() => navigate(to)} className="flex-none overflow-hidden" style={{ width: phone ? 72 : 96, height: phone ? 72 : 96, borderRadius: 16, background: event.posterUrl ? '#111' : posterPlaceholder(event.id || event.title), border: 'none', padding: 0, cursor: 'pointer' }} aria-label={event.title}>
        {event.posterUrl && <img src={event.posterUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </button>
      <button type="button" onClick={() => navigate(to)} className="flex min-w-0 flex-col text-left" style={{ flex: '1 1 200px', gap: 6, border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'inherit', fontFamily: 'inherit' }}>
        <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{whenLabel(event.dateTime).replace(',', ' ·')}</span>
          <TagPill bg={sBg} fg={sFg} data-testid={`event-status-${event.id}`}>{eventStatusLabel(event.status)}</TagPill>
          {event.isMembersOnly && <TagPill bg="#fff" fg="#000" style={{ fontWeight: 700 }}>MEMBERS ONLY</TagPill>}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{event.title}</span>
        {meta && <span className="uppercase" style={{ fontSize: 10, letterSpacing: '.1em', color: MUTED }}>{meta}</span>}
      </button>
      <span className="flex flex-none flex-wrap items-center" style={{ gap: 6 }}>
        {cta && upcoming && !phone && (
          <PillButton variant={ctaFill ? 'primary' : 'ghost'} size={38} fontSize={10} onClick={() => navigate(to)}>{String(event.status).toLowerCase() === 'funding' ? 'CHIP IN' : 'GET TICKETS'}</PillButton>
        )}
        {admin && (
          <>
            <PillButton variant="ghost" size={32} fontSize={9} onClick={() => onHide?.(event)} data-testid={`event-hide-${event.id}`}>{hidden ? 'UNHIDE' : 'HIDE'}</PillButton>
            <PillButton variant={armedUnlink ? 'armed' : 'ghost'} size={32} fontSize={9} style={armedUnlink ? undefined : { color: RED }} onClick={() => onUnlink?.(event)} data-testid={`event-unlink-${event.id}`}>{armedUnlink ? 'UNLINK?' : 'UNLINK'}</PillButton>
          </>
        )}
      </span>
    </div>
  );
}
